/**
 * Implementación nativa de debounce
 * Retrasa la ejecución de una función hasta que haya pasado un tiempo determinado
 * desde la última vez que fue invocada
 * 
 * @param func - Función a debounce
 * @param wait - Tiempo de espera en milisegundos
 * @param immediate - Si true, ejecuta la función inmediatamente en la primera llamada
 * @returns Función debounced con método cancel
 */
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate = false,
): T & { cancel: () => void } => {
  let timeout: NodeJS.Timeout | null = null;
  let result: ReturnType<T>;

  const debounced = function (this: any, ...args: Parameters<T>): ReturnType<T> {
    const context = this;

    const later = () => {
      timeout = null;
      if (!immediate) {
        result = func.apply(context, args);
      }
    };

    const callNow = immediate && !timeout;

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);

    if (callNow) {
      result = func.apply(context, args);
    }

    return result;
  } as T & { cancel: () => void };

  // Método para cancelar el debounce
  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
};

export default debounce;
